import { useState } from 'react';
import { Platform, Pressable, PressableProps, View, StyleSheet, type ViewStyle } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import { cn } from '@/lib/cn';
import { webStyle } from '@/lib/platform';

interface AnimatedPressableProps extends Omit<PressableProps, 'children' | 'style'> {
  className?: string;
  pressScale?: number;
  children: React.ReactNode;
  style?: PressableProps['style'];
}

const isWeb = Platform.OS === 'web';

/**
 * Drop-in Pressable replacement with polished press feedback.
 * - Web: CSS transition for scale + opacity, hover state
 * - iOS/Android: Animated.View wrapper with reanimated spring scale
 * - Android: additional native ripple
 */
export default function AnimatedPressable({
  className,
  pressScale = 0.97,
  children,
  disabled,
  onPress,
  style,
  ...rest
}: AnimatedPressableProps) {
  // Native animation hooks — must be called unconditionally (rules of hooks)
  const scale = useSharedValue(1);

  const animStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  // The fill variant carries its OWN flex, inside the worklet. It deliberately
  // is not `style={[animStyle, { flex: 1 }]}` — that array loses the static
  // entry and collapses the node to height 0. Measured, see the fill branch.
  const animFillStyle = useAnimatedStyle(() => ({
    flex: 1,
    transform: [{ scale: scale.value }],
  }));

  if (isWeb) {
    return (
      <WebAnimatedPressable
        className={className}
        pressScale={pressScale}
        disabled={disabled}
        onPress={onPress}
        style={style}
        {...rest}
      >
        {children}
      </WebAnimatedPressable>
    );
  }

  // Native path: Animated.View wraps Pressable for spring animation

  const handlePressIn = () => {
    if (!disabled && pressScale !== 1) {
      scale.value = withSpring(pressScale, { damping: 15, stiffness: 300 });
    }
  };

  const handlePressOut = () => {
    if (!disabled && pressScale !== 1) {
      scale.value = withSpring(1, { damping: 12, stiffness: 200 });
    }
  };

  const renderPressable = (pressableStyle: PressableProps['style']) => (
    <Pressable
      className={cn(className)}
      disabled={disabled}
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      android_ripple={
        !disabled ? { color: 'rgba(0,0,0,0.06)', borderless: false } : undefined
      }
      style={pressableStyle}
      {...rest}
    >
      {children}
    </Pressable>
  );

  // A flex button (flex/flexGrow/flexBasis) must FILL its share of the parent.
  // This component inserts a node between the caller's wrapper and the Pressable,
  // so a definite height has to travel THROUGH that node to reach the content.
  // Every link in the chain must therefore be definite; one content-sized link
  // collapses the whole thing to zero.
  //
  // FILL INTENT ARRIVES TWO WAYS, and both must be recognised.
  //
  //   style={{ flex: 1 }}          ← hoisted onto the plain layout View below
  //   className="flex-1"           ← NativeWind puts flex on the Pressable, so
  //                                  there is nothing to hoist; we supply one
  //
  // This is exactly the shape `<pressable_sizing>` in CLAUDE.md prescribes
  // (wrapper owns the height, Pressable fills it). The guidance is right; it
  // needs help surviving this component's extra layout node, so the fix belongs
  // here rather than in every caller.
  //
  // TWO SEPARATE DEFECTS PRODUCED THE SAME BLANK BUTTON, a year apart in the
  // same file. Both showed as a solid coloured block with no label:
  //
  //   2026-08-09  `MessageFounderButton` — fill intent expressed as
  //               `className="flex-1"` was not detected at all, so the branch
  //               below never ran. Fixed by `classNameWantsFill`.
  //   2026-08-17  `StrategicAnalysisEntry` — the branch DID run, and the
  //               animated node still collapsed, because its flex was passed
  //               beside the animated style instead of inside it. See the
  //               measurements at that node.
  //
  // The first fix was verified by asserting the source and the rendered style
  // objects. Both assertions passed while the button was blank on a device:
  // jest has no Yoga, so a node carrying `flex: 1` that computes to 0 looks
  // identical to one that works. Layout claims about this component need a
  // device measurement, not a green test.
  const flat = typeof style === 'function' ? null : (StyleSheet.flatten(style) as ViewStyle | undefined);
  const styleWantsFill = !!flat && (flat.flex != null || flat.flexGrow != null || flat.flexBasis != null);
  const classNameWantsFill = /(?:^|\s)(?:flex-1|flex-grow|grow)(?:\s|$)/.test(className ?? '');

  if (styleWantsFill || classNameWantsFill) {
    const layoutStyle: ViewStyle = {};
    const visualStyle: ViewStyle = { ...(flat ?? {}) };
    (['flex', 'flexGrow', 'flexShrink', 'flexBasis', 'alignSelf'] as const).forEach((k) => {
      if (flat?.[k] != null) {
        (layoutStyle as Record<string, unknown>)[k] = flat[k];
        delete (visualStyle as Record<string, unknown>)[k];
      }
    });
    // Fill intent expressed only in className has no flex keys to hoist, so the
    // outer View has to supply one or it stays content-sized and nothing changes.
    if (layoutStyle.flex == null && layoutStyle.flexGrow == null && layoutStyle.flexBasis == null) {
      layoutStyle.flex = 1;
    }

    return (
      <View style={layoutStyle}>
        {/* THE FLEX MUST LIVE INSIDE THE WORKLET, NOT BESIDE IT.
            `style={[animStyle, { flex: 1 }]}` — the canonical reanimated shape,
            and what shipped here — loses the static entry: the node computes to
            height 0 and takes the Pressable, label and icon down with it.
            Measured on device (iPhone 17 Pro, reanimated v4), parent 332x44:

              [animStyle, { flex: 1 }]            -> 332 x 0
              [{ flex: 1 }, animStyle]            -> 332 x 0   (not an ordering bug)
              [animStyle, { height: '100%' }]     -> 332 x 0   (not specific to flex)
              { flex: 1 }  (no animated style)    -> 332 x 44
              useAnimatedStyle(() => ({flex: 1})) -> 332 x 44  <- this

            So the animated node CAN flex; it just cannot be handed layout
            through a style array. One object, produced by the worklet, works. */}
        <Animated.View style={animFillStyle}>
          {renderPressable([visualStyle, { flex: 1 }])}
        </Animated.View>
      </View>
    );
  }

  return <Animated.View style={animStyle}>{renderPressable(style)}</Animated.View>;
}

/**
 * Web-only subcomponent — uses state-based press/hover tracking
 * so that the `style` prop stays a plain object (not a function),
 * which avoids conflicts with NativeWind className processing.
 */
function WebAnimatedPressable({
  className,
  pressScale = 0.97,
  children,
  disabled,
  onPress,
  style,
  ...rest
}: AnimatedPressableProps) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      className={cn(className)}
      disabled={disabled}
      onPress={onPress}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        typeof style === 'function' ? undefined : style,
        webStyle(
          {
            transform: pressed && !disabled ? [{ scale: pressScale }] : [{ scale: 1 }],
            opacity: disabled ? 0.5 : pressed ? 0.9 : hovered ? 0.85 : 1,
          },
          {
            transition: 'transform 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 150ms ease',
            cursor: disabled ? 'default' : 'pointer',
          },
        ),
      ]}
      {...rest}
    >
      {children}
    </Pressable>
  );
}
